import { Head, Link, router } from "@inertiajs/react"
import { AlertCircle, AppWindow, Clock, Server, Trash2 } from "lucide-react"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { appPath, dashboardPath, serverPath } from "@/routes"
import type { App, BreadcrumbItem, HealthCheck, Server as ServerType } from "@/types"
import React from "react"

interface HealthChecksIndexProps {
  health_checks: (HealthCheck & {
    app?: Pick<App, "id" | "name">
    server?: Pick<ServerType, "id" | "name">
  })[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: dashboardPath() },
  { title: "Health Issues", href: "#" },
]

function formatDate(date: string | null | undefined): string {
  if (!date) return "N/A"
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getCheckTypeLabel(checkType: string): string {
  const labels: Record<string, string> = {
    url: "URL Check",
    disk: "Disk Usage",
    ssl: "SSL Certificate",
    robots: "SEO Check",
  }
  return labels[checkType] || checkType
}

function getStatusMessage(check: HealthCheck): string {
  if (check.status === "warning") {
    if (check.check_type === "ssl") {
      const expiresAt = check.result?.expires_at as string | undefined
      if (expiresAt) {
        const daysUntilExpiry = Math.floor(
          (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
        return `SSL certificate expires in ${daysUntilExpiry} days`
      }
    }
    if (check.check_type === "robots") {
      const issues = check.result?.issues as Array<{type: string, message: string, severity: string}> | undefined
      if (issues && issues.length > 0) {
        return issues.map(i => i.message).join("; ")
      }
    }
    return "Warning detected"
  }
  if (check.status === "error") {
    if (check.check_type === "url") {
      const error = check.result?.error
      const errorMsg = typeof error === "string" ? error : "Unknown error"
      return `URL check failed: ${errorMsg}`
    }
    if (check.check_type === "disk") {
      const usagePercent = check.result?.usage_percent
      const usage = typeof usagePercent === "number" ? usagePercent.toString() : "N/A"
      return `Disk usage critical: ${usage}%`
    }
    if (check.check_type === "robots") {
      const issues = check.result?.issues as Array<{type: string, message: string, severity: string}> | undefined
      if (issues && issues.length > 0) {
        return issues.map(i => i.message).join("; ")
      }
      const error = check.result?.error
      if (error) {
        const errorMsg = typeof error === "string" ? error : "Unknown error"
        return `SEO check failed: ${errorMsg}`
      }
    }
    return "Error detected"
  }
  return "No message"
}

export default function HealthChecksIndex({ health_checks }: HealthChecksIndexProps) {
  function handleDelete(check: HealthCheck) {
    if (confirm(`Are you sure you want to delete this alert?`)) {
      router.delete(`/health_checks/${check.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Health Issues" />

      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <h1 className="text-xl font-bold">Health Issues</h1>
            <span className="text-muted-foreground text-sm">
              ({health_checks.length})
            </span>
          </div>
        </div>

        {health_checks.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-muted-foreground text-center text-sm">
                No health issues found
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            {health_checks.map((check) => (
              <Card key={check.id} className="overflow-hidden">
                <div className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {getCheckTypeLabel(check.check_type)}
                        </span>
                        <StatusBadge status={check.status ?? "ok"} />
                      </div>
                      
                      <div className="text-muted-foreground mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        {check.app && (
                          <Link
                            href={appPath(check.app.id)}
                            className="flex items-center gap-1 hover:underline"
                          >
                            <AppWindow className="h-3 w-3" />
                            {check.app.name}
                          </Link>
                        )}
                        {check.server && (
                          <Link
                            href={serverPath(check.server.id)}
                            className="flex items-center gap-1 hover:underline"
                          >
                            <Server className="h-3 w-3" />
                            {check.server.name}
                          </Link>
                        )}
                        {check.checked_at && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(check.checked_at)}
                          </span>
                        )}
                      </div>

                      <p className="text-sm">{getStatusMessage(check)}</p>

                      {check.result && Object.keys(check.result).length > 0 && (
                        <dl className="text-muted-foreground mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                          {(check.target && check.target != "/") && (<React.Fragment key="target">
                              <dt className="font-medium">target:</dt>
                              {check.target.startsWith("http") ? (
                                <Link href={check.target} className="text-blue-500 hover:underline">
                                  {check.target}
                                </Link>
                              ) : (
                                <span>{check.target}</span>
                              )}
                            </React.Fragment>
                          )}
                          {Object.entries(check.result).map(([key, value]) => (
                            <React.Fragment key={`${key}-label`}>
                              <dt key={`${key}-label`} className="font-medium">
                                {key}:
                              </dt>
                              <dd key={`${key}-value`}>{String(value)}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => handleDelete(check)}
                    >
                      <Trash2 className="text-destructive h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
